import { Users } from 'lucide-react';
import { palette, transition, fontFamily } from '../../../styles/tokens';
import { dotColor, statusLabel } from '../hooks/useConversationSummaries';
import type { MergedRow, ConversationSummary } from '../types';

interface Props {
  row: MergedRow;
  summary?: ConversationSummary;
  isActive: boolean;
  onClick: () => void;
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-CA', { weekday: 'short' });
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function initials(name: string): string {
  const parts = name.split(' ');
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

// Deterministic gradient from name (matches mobile AvatarGradient pattern)
const GRADIENTS = [
  ['#6CB4EE', '#4A90D9'],
  ['#12B76A', '#0E9255'],
  ['#F59E0B', '#D97706'],
  ['#8B5CF6', '#7C3AED'],
  ['#EC4899', '#DB2777'],
  ['#14B8A6', '#0D9488'],
  ['#F97316', '#EA580C'],
];

function nameGradient(name: string): string[] {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return GRADIENTS[Math.abs(h) % GRADIENTS.length];
}

export function ConversationRow({ row, summary, isActive, onClick }: Props) {
  const [g1, g2] = nameGradient(row.name);
  const isGroup = row.kind === 'group';
  const status = summary ? statusLabel(summary) : null;

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        width: '100%', padding: '11px 16px', border: 'none', cursor: 'pointer',
        background: isActive ? palette.accentWash : 'transparent',
        fontFamily, textAlign: 'left',
        transition: `background ${transition.fast}`,
        opacity: row.shelved ? 0.55 : 1,
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = palette.g50; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Avatar — gradient initials for DMs, accent group glyph for groups */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        {isGroup ? (
          <div style={{
            width: 42, height: 42, borderRadius: 21,
            background: palette.accentWash,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Users size={19} color={palette.accent} strokeWidth={1.9} />
          </div>
        ) : (
          <div style={{
            width: 42, height: 42, borderRadius: 21,
            background: `linear-gradient(135deg, ${g1}, ${g2})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13.5, fontWeight: 600, color: palette.white,
          }}>
            {initials(row.name)}
          </div>
        )}
        {row.unread && (
          <div style={{
            position: 'absolute', top: 0, right: -1,
            width: 9, height: 9, borderRadius: 5,
            background: palette.accent, border: `2px solid ${palette.white}`,
          }} />
        )}
      </div>

      {/* Name + preview + status */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{
            fontSize: 14,
            fontWeight: row.unread ? 700 : 500,
            color: palette.g800,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontStyle: row.shelved ? 'italic' : 'normal',
          }}>
            {row.name}
          </span>
          <span style={{ fontSize: 11, color: palette.g400, flexShrink: 0 }}>
            {formatTime(row.lastMessageAt)}
          </span>
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{
            display: 'block',
            fontSize: 12.5, color: row.unread ? palette.g700 : palette.g500,
            fontWeight: row.unread ? 500 : 400,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.shelved
              ? 'Shelved'
              : row.preview ?? (isGroup ? `${row.memberCount ?? 0} members` : 'No messages yet')}
          </span>
        </div>
        {/* §T.157 status line — colored dot + derived label */}
        {status && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
            <span style={{
              width: 6, height: 6, borderRadius: 3, flexShrink: 0,
              background: dotColor(summary!.dot),
            }} />
            <span style={{
              fontSize: 11.5, color: palette.g600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {status}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
